import type { DeviceState } from '@cenorion/api/generated/hooks';

/** The order the results panel always shows its groups in, whatever the counts. */
export const SEARCH_GROUPS = ['DEVICE', 'WORKFLOW', 'PLAN', 'CONFIGURATION'] as const;
export type SearchGroupType = (typeof SEARCH_GROUPS)[number];

/** Rows a group shows before it offers "View all". */
export const SEARCH_GROUP_LIMIT = 5;

/** Below this many characters the box searches nothing: one letter matches half the tenant. */
export const SEARCH_MIN_LENGTH = 2;

export const SEARCH_SERVICES = ['dfpost', 'dfpre', 'sc', 'sim'] as const;
export type SearchService = (typeof SEARCH_SERVICES)[number];

/** The settings area a configuration result belongs to, as the API names it. */
export const CONFIGURATION_AREAS = ['USERS', 'ROLES', 'WHITELIST', 'APP_CUSTOMISATION'] as const;
export type ConfigurationArea = (typeof CONFIGURATION_AREAS)[number];

export type DeviceSearchResult = {
  type: 'DEVICE';
  id: string;
  /** IMEI, or the serial for a device without one. */
  identifier: string;
  /** Null when the device is not enrolled. */
  service: SearchService | null;
  /** Null when the device has no plan. */
  plan: string | null;
  /** As the platform sends it, the way the registry's STATE badge shows it. */
  state: DeviceState;
};

export type WorkflowSearchResult = {
  type: 'WORKFLOW';
  id: string;
  name: string;
  deviceCount: number;
  updatedAt: Date;
};

export type PlanSearchResult = {
  type: 'PLAN';
  id: string;
  name: string;
  /** Null for a service this portal cannot label yet. */
  service: SearchService | null;
  deviceCount: number;
};

export type ConfigurationSearchResult = {
  type: 'CONFIGURATION';
  id: string;
  name: string;
  area: ConfigurationArea;
  /** A user's email, so a match on it shows; null for every other area. */
  email: string | null;
};

export type SearchResult =
  DeviceSearchResult | WorkflowSearchResult | PlanSearchResult | ConfigurationSearchResult;

export type SearchResultGroup = {
  type: SearchGroupType;
  /** Every match, not just the rows served: "View all" shows when it exceeds them. */
  total: number;
  /** At most `SEARCH_GROUP_LIMIT`. */
  items: SearchResult[];
};
